import { createContext, useContext, useEffect, useState } from "react"

import type { useSearchParams } from "react-router-dom"

type FilterState = Record<string, any>

export interface FiltersContextProps {
  filters: Record<string, Value>
  setFilters: (filters: Record<string, Value>) => void
  onChange: (values: { name: string; value: Value }) => void
  searchParams: ReturnType<typeof useSearchParams>[0]
  searchParamsProps: {
    isDirty: boolean
    original: ReturnType<typeof useSearchParams>[0]
    updateSearchParams: (
      params: URLSearchParams | ((params: URLSearchParams) => URLSearchParams)
    ) => void
  }
  setSearchParams: (cb: (params: URLSearchParams) => URLSearchParams) => void
  useQueryString?: boolean
}

export const getFilterState = (values: FilterState = {}, dirty = false) => {
  const allowClear = Object.keys(values).some((key) => values[key])

  return {
    dirty,
    values,
    allowClear,
    changed: false
  }
}

export const useStateFilter = (
  initialState: FilterState,
  props: {
    onChangeState?: (
      prev: Record<string, string> | URLSearchParams,
      state: FilterState
    ) => URLSearchParams
    setSearchParams?: (
      cb: (prev: Record<string, string> | URLSearchParams) => URLSearchParams
    ) => void
  } = {}
) => {
  const [state, setState] = useState(getFilterState(initialState))

  useEffect(() => {
    const { onChangeState, setSearchParams } = props

    if (state.dirty && onChangeState && setSearchParams)
      setSearchParams((prev) => onChangeState(prev, state.values))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const onChange = (value: FilterState) => {
    const nextState = { ...state.values, ...value }

    setState({ ...state, ...getFilterState(nextState, true), changed: true })
  }

  return {
    filterState: state,
    reset: (values: FilterState, dirty = true) => {
      setState(getFilterState(values, dirty))
    },
    markDirty: () => {
      setState((prevState) => ({ ...prevState, changed: true, dirty: true }))
    },
    onFilterStateChange: onChange,
    onClear: () => {
      setState(getFilterState({}, true))
    },
    onOk: () => {
      setState((prevState) => ({ ...prevState, dirty: false }))
    }
  }
}

export type Value = undefined | string | (boolean | number | string)[]

export const FiltersContext = createContext<FiltersContextProps>({
  filters: {},
  setFilters: () => {},
  onChange: () => {},
  searchParams: new URLSearchParams(),
  searchParamsProps: {
    isDirty: false,
    original: new URLSearchParams(),
    updateSearchParams: () => {}
  },
  setSearchParams: () => new URLSearchParams()
})

export const useFilters = () => {
  const context = useContext(FiltersContext)

  return context
}
